import React, { useState } from 'react';
import { Match, GroupMemberProfile, PlayerMatchStat } from '../types';
import { X, CheckCircle2, Award, Sparkles, Goal, Shield, Star, Users, Plus, Minus, Trophy } from 'lucide-react';
import { calculateStatAssistedMatchRating } from '../services/ratingCalculator';
import { NumberInput } from './NumberInput';

interface PostMatchReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  match: Match;
  members: GroupMemberProfile[];
  onPublishReport: (matchId: string, resultData: {
    teamAScore: number;
    teamBScore: number;
    playerStats: PlayerMatchStat[];
    motmWinnerMemberId?: string;
  }) => void;
}

export const PostMatchReportModal: React.FC<PostMatchReportModalProps> = ({
  isOpen,
  onClose,
  match,
  members,
  onPublishReport,
}) => {
  const [step, setStep] = useState<1 | 2 | 3 | 4 | 5>(1);

  // Step 1: Score
  const [teamAScore, setTeamAScore] = useState(match.result?.teamAScore || 0);
  const [teamBScore, setTeamBScore] = useState(match.result?.teamBScore || 0);

  // Separate players into Team A and Team B
  const teamAPlayers = match.teamA?.players || [];
  const teamBPlayers = match.teamB?.players || [];
  const teamAPlayerIds = teamAPlayers.map((p) => p.memberProfileId);
  const teamBPlayerIds = teamBPlayers.map((p) => p.memberProfileId);
  const playingPlayerIds = Array.from(new Set([...teamAPlayerIds, ...teamBPlayerIds]));

  const playingMembers = playingPlayerIds.length > 0
    ? members.filter((m) => playingPlayerIds.includes(m.id))
    : members;

  const teamAMembers = playingMembers.filter((m) => teamAPlayerIds.includes(m.id));
  const teamBMembers = playingMembers.filter((m) => teamBPlayerIds.includes(m.id));
  const unassignedMembers = playingMembers.filter((m) => !teamAPlayerIds.includes(m.id) && !teamBPlayerIds.includes(m.id));

  // Step 2 & 3: Stats state per player
  const [playerStatsMap, setPlayerStatsMap] = useState<{ [memberId: string]: PlayerMatchStat }>(() => {
    const map: { [memberId: string]: PlayerMatchStat } = {};
    playingMembers.forEach((m) => {
      map[m.id] = {
        memberProfileId: m.id,
        goals: 0,
        assists: 0,
        yellowCards: 0,
        redCards: 0,
        saves: 0,
        rating: 6.5,
      };
    });
    return map;
  });

  // Step 4: MOTM
  const [motmWinnerId, setMotmWinnerId] = useState<string>(
    match.motmWinnerMemberId || playingMembers[0]?.id || ''
  );

  // Helper to update individual player stat
  type NumericStatKey = Exclude<keyof PlayerMatchStat, 'memberProfileId'>;

  const updateStat = (memberId: string, key: NumericStatKey, val: number) => {
    setPlayerStatsMap((prev) => ({
      ...prev,
      [memberId]: {
        ...prev[memberId],
        [key]: Math.max(0, val),
      },
    }));
  };

  const adjustStat = (memberId: string, key: NumericStatKey, delta: number) => {
    setPlayerStatsMap((prev) => {
      const currentVal = prev[memberId]?.[key] ?? 0;
      return {
        ...prev,
        [memberId]: {
          ...prev[memberId],
          [key]: Math.max(0, currentVal + delta),
        },
      };
    });
  };

  // Calculate live team goal totals
  const totalTeamAGoalsEntered = teamAMembers.reduce((acc, m) => acc + (playerStatsMap[m.id]?.goals || 0), 0);
  const totalTeamBGoalsEntered = teamBMembers.reduce((acc, m) => acc + (playerStatsMap[m.id]?.goals || 0), 0);

  // Auto-calculate stat-assisted match ratings
  const handleAutoCalculateRatings = () => {
    setPlayerStatsMap((prev) => {
      const nextMap = { ...prev };
      Object.keys(nextMap).forEach((id) => {
        const pStat = nextMap[id];
        const mem = members.find((m) => m.id === id);
        const isGk = mem?.primaryPosition === 'GK';
        const isMotm = id === motmWinnerId;
        const isTeamA = teamAPlayerIds.includes(id);

        const calculated = calculateStatAssistedMatchRating({
          goals: pStat.goals,
          assists: pStat.assists,
          yellowCards: pStat.yellowCards,
          redCards: pStat.redCards,
          saves: pStat.saves,
          isCleanSheet: isGk ? (isTeamA ? teamBScore === 0 : teamAScore === 0) : false,
          isMotm,
          isGk,
        });

        nextMap[id] = {
          ...pStat,
          rating: calculated,
        };
      });
      return nextMap;
    });
  };

  const handleFinish = () => {
    onPublishReport(match.id, {
      teamAScore,
      teamBScore,
      playerStats: Object.values(playerStatsMap),
      motmWinnerMemberId: motmWinnerId,
    });
    onClose();
  };

  // Helper for rating color styling
  const getRatingBadgeClass = (rating: number) => {
    if (rating >= 8.5) return 'text-emerald-400 border-emerald-400/30 bg-emerald-400/10';
    if (rating >= 7.0) return 'text-[#a8c936] border-[#a8c936]/30 bg-[#a8c936]/10';
    if (rating >= 6.0) return 'text-amber-400 border-amber-400/30 bg-amber-400/10';
    return 'text-red-400 border-red-400/30 bg-red-400/10';
  };

  const renderPlayerStatRow = (m: GroupMemberProfile, isTeamA?: boolean) => {
    const pStat = playerStatsMap[m.id] || { goals: 0, assists: 0, yellowCards: 0, redCards: 0, rating: 6.5 };
    return (
      <div key={m.id} className="bg-[#171c19] p-3 rounded-xl border border-white/5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <img src={m.avatar || 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100'} alt={m.displayName} className="w-9 h-9 rounded-full object-cover border border-white/10" />
          <div>
            <div className="flex items-center gap-1.5">
              <span className="font-semibold text-xs text-white">{m.displayName}</span>
              <span className="text-[10px] font-mono text-[#a8c936] bg-[#a8c936]/10 px-1.5 py-0.2 rounded border border-[#a8c936]/20">
                #{m.jerseyNumber}
              </span>
            </div>
            <span className="text-[10px] text-slate-400 font-mono">{m.primaryPosition}</span>
          </div>
        </div>

        <div className="flex items-center gap-4 text-xs font-mono justify-between sm:justify-end">
          {/* Goals Input */}
          <div className="flex items-center gap-1.5 bg-[#080b0a] p-1 rounded-lg border border-white/10">
            <span className="text-slate-400 text-[10px] font-sans pl-1">⚽ Goals</span>
            <button
              type="button"
              onClick={() => adjustStat(m.id, 'goals', -1)}
              className="w-6 h-6 bg-white/5 hover:bg-white/10 rounded flex items-center justify-center text-slate-300 font-bold"
            >
              <Minus className="w-3 h-3" />
            </button>
            <span className="w-6 text-center font-bold text-white text-xs">{pStat.goals}</span>
            <button
              type="button"
              onClick={() => adjustStat(m.id, 'goals', 1)}
              className="w-6 h-6 bg-[#a8c936]/20 hover:bg-[#a8c936]/30 text-[#a8c936] rounded flex items-center justify-center font-bold"
            >
              <Plus className="w-3 h-3" />
            </button>
          </div>

          {/* Assists Input */}
          <div className="flex items-center gap-1.5 bg-[#080b0a] p-1 rounded-lg border border-white/10">
            <span className="text-slate-400 text-[10px] font-sans pl-1">🎯 Assists</span>
            <button
              type="button"
              onClick={() => adjustStat(m.id, 'assists', -1)}
              className="w-6 h-6 bg-white/5 hover:bg-white/10 rounded flex items-center justify-center text-slate-300 font-bold"
            >
              <Minus className="w-3 h-3" />
            </button>
            <span className="w-6 text-center font-bold text-white text-xs">{pStat.assists}</span>
            <button
              type="button"
              onClick={() => adjustStat(m.id, 'assists', 1)}
              className="w-6 h-6 bg-white/10 hover:bg-white/20 text-white rounded flex items-center justify-center font-bold"
            >
              <Plus className="w-3 h-3" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4">
      <div className="bg-[#121614] border border-white/10 rounded-2xl w-full max-w-2xl p-4 sm:p-6 shadow-2xl overflow-y-auto max-h-[92vh] text-white">
        
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <div>
            <span className="text-xs font-bold text-[#a8c936] uppercase tracking-wider block">Match Completion Flow</span>
            <h3 className="font-bold text-lg text-white">{match.title}</h3>
          </div>
          <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-white/5 transition">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Step Indicator */}
        <div className="flex items-center gap-1 bg-[#171c19] p-1.5 rounded-xl border border-white/5 mb-5 text-xs font-semibold overflow-x-auto">
          {[
            { s: 1, label: '1. Score' },
            { s: 2, label: '2. Goals & Assists' },
            { s: 3, label: '3. Player Ratings' },
            { s: 4, label: '4. MOTM' },
            { s: 5, label: '5. Publish' },
          ].map((item) => (
            <button
              key={item.s}
              onClick={() => setStep(item.s as any)}
              className={`flex-1 min-w-[75px] py-1.5 rounded-lg text-center transition ${
                step === item.s
                  ? 'bg-[#1c221d] text-[#a8c936] border border-white/10'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>

        {/* STEP 1: SCORE */}
        {step === 1 && (
          <div className="space-y-6 py-2">
            <div className="bg-[#171c19] p-6 rounded-2xl border border-white/5 flex items-center justify-around text-center">
              <div>
                <span className="text-xs font-bold text-slate-300 block mb-2">{match.teamA?.name || 'Team A'}</span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setTeamAScore((s) => Math.max(0, s - 1))}
                    className="w-10 h-10 rounded-xl bg-white/5 hover:bg-white/10 text-white font-bold text-lg flex items-center justify-center border border-white/10"
                  >
                    -
                  </button>
                  <NumberInput
                    value={teamAScore}
                    onCommit={setTeamAScore}
                    min={0}
                    className="w-16 h-14 bg-[#080b0a] border border-white/10 text-2xl font-extrabold text-center font-mono rounded-xl text-white focus:border-[#a8c936]"
                    aria-label="Team A score"
                  />
                  <button
                    type="button"
                    onClick={() => setTeamAScore((s) => s + 1)}
                    className="w-10 h-10 rounded-xl bg-[#a8c936]/20 hover:bg-[#a8c936]/30 text-[#a8c936] font-bold text-lg flex items-center justify-center border border-[#a8c936]/30"
                  >
                    +
                  </button>
                </div>
              </div>

              <span className="text-2xl font-black text-slate-500 font-mono">VS</span>

              <div>
                <span className="text-xs font-bold text-slate-300 block mb-2">{match.teamB?.name || 'Team B'}</span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setTeamBScore((s) => Math.max(0, s - 1))}
                    className="w-10 h-10 rounded-xl bg-white/5 hover:bg-white/10 text-white font-bold text-lg flex items-center justify-center border border-white/10"
                  >
                    -
                  </button>
                  <NumberInput
                    value={teamBScore}
                    onCommit={setTeamBScore}
                    min={0}
                    className="w-16 h-14 bg-[#080b0a] border border-white/10 text-2xl font-extrabold text-center font-mono rounded-xl text-white focus:border-[#a8c936]"
                    aria-label="Team B score"
                  />
                  <button
                    type="button"
                    onClick={() => setTeamBScore((s) => s + 1)}
                    className="w-10 h-10 rounded-xl bg-[#a8c936]/20 hover:bg-[#a8c936]/30 text-[#a8c936] font-bold text-lg flex items-center justify-center border border-[#a8c936]/30"
                  >
                    +
                  </button>
                </div>
              </div>
            </div>

            <button
              onClick={() => setStep(2)}
              className="w-full py-3 bg-[#a8c936] hover:bg-[#b8db3d] text-[#080b0a] font-bold text-sm rounded-xl transition"
            >
              Next: Enter Goals & Assists
            </button>
          </div>
        )}

        {/* STEP 2: GOALS & ASSISTS */}
        {step === 2 && (
          <div className="space-y-4">
            {/* Live Tally Header */}
            <div className="bg-[#171c19] p-3 rounded-xl border border-white/5 flex items-center justify-between text-xs font-mono">
              <div className="flex items-center gap-2">
                <span className="text-slate-400">{match.teamA?.name || 'Team A'} Goals:</span>
                <span className={`font-bold ${totalTeamAGoalsEntered === teamAScore ? 'text-[#a8c936]' : 'text-amber-400'}`}>
                  {totalTeamAGoalsEntered} / {teamAScore}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-slate-400">{match.teamB?.name || 'Team B'} Goals:</span>
                <span className={`font-bold ${totalTeamBGoalsEntered === teamBScore ? 'text-[#a8c936]' : 'text-amber-400'}`}>
                  {totalTeamBGoalsEntered} / {teamBScore}
                </span>
              </div>
            </div>

            <div className="space-y-4 max-h-[52vh] overflow-y-auto pr-1">
              {/* Team A Section */}
              {teamAMembers.length > 0 && (
                <div className="space-y-2">
                  <span className="text-xs font-bold text-[#a8c936] uppercase tracking-wider block">
                    {match.teamA?.name || 'Team A'} Players
                  </span>
                  {teamAMembers.map((m) => renderPlayerStatRow(m, true))}
                </div>
              )}

              {/* Team B Section */}
              {teamBMembers.length > 0 && (
                <div className="space-y-2">
                  <span className="text-xs font-bold text-blue-400 uppercase tracking-wider block">
                    {match.teamB?.name || 'Team B'} Players
                  </span>
                  {teamBMembers.map((m) => renderPlayerStatRow(m, false))}
                </div>
              )}

              {/* Unassigned / All Members */}
              {unassignedMembers.length > 0 && (
                <div className="space-y-2">
                  <span className="text-xs font-bold text-slate-400 uppercase tracking-wider block">
                    Participating Players
                  </span>
                  {unassignedMembers.map((m) => renderPlayerStatRow(m))}
                </div>
              )}
            </div>

            <button
              onClick={() => setStep(3)}
              className="w-full py-3 bg-[#a8c936] hover:bg-[#b8db3d] text-[#080b0a] font-bold text-sm rounded-xl transition"
            >
              Next: Rate Performances
            </button>
          </div>
        )}

        {/* STEP 3: PLAYER RATINGS */}
        {step === 3 && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-xs font-semibold text-slate-400">Match Ratings (1.0 to 10.0)</span>
              <button
                onClick={handleAutoCalculateRatings}
                className="px-3 py-1 bg-white/5 hover:bg-white/10 text-[#a8c936] text-xs font-semibold rounded-lg border border-white/5 transition flex items-center gap-1"
              >
                <Sparkles className="w-3.5 h-3.5 text-[#a8c936]" /> Stat-Assisted Auto Rating
              </button>
            </div>

            <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1">
              {playingMembers.map((m) => {
                const pStat = playerStatsMap[m.id] || { goals: 0, assists: 0, rating: 6.5 };
                const ratingBadge = getRatingBadgeClass(pStat.rating);
                return (
                  <div key={m.id} className="bg-[#171c19] p-3 rounded-xl border border-white/5 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2.5">
                      <img src={m.avatar || 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100'} alt={m.displayName} className="w-8 h-8 rounded-full object-cover border border-white/10" />
                      <div>
                        <div className="flex items-center gap-1.5">
                          <span className="font-semibold text-xs text-white">{m.displayName}</span>
                          {(pStat.goals > 0 || pStat.assists > 0) && (
                            <span className="text-[10px] text-[#a8c936] font-mono font-bold bg-[#a8c936]/10 px-1 py-0.2 rounded">
                              {pStat.goals > 0 && `⚽${pStat.goals} `}{pStat.assists > 0 && `🎯${pStat.assists}`}
                            </span>
                          )}
                        </div>
                        <span className="text-[10px] text-slate-400 font-mono">{m.primaryPosition}</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      {/* Stepper buttons for Rating */}
                      <div className="flex items-center gap-1.5 bg-[#080b0a] p-1 rounded-lg border border-white/10">
                        <button
                          type="button"
                          onClick={() => updateStat(m.id, 'rating', Math.max(1.0, parseFloat((pStat.rating - 0.5).toFixed(1))))}
                          className="w-6 h-6 bg-white/5 hover:bg-white/10 rounded flex items-center justify-center text-slate-300 font-bold"
                        >
                          <Minus className="w-3 h-3" />
                        </button>

                        <NumberInput
                          value={pStat.rating}
                          onCommit={(v) => updateStat(m.id, 'rating', v)}
                          allowDecimal
                          min={1}
                          max={10}
                          emptyDefault={6.5}
                          className={`w-14 bg-transparent text-center font-bold font-mono text-sm rounded p-0.5 border ${ratingBadge}`}
                          aria-label={`Rating for ${m.displayName}`}
                        />

                        <button
                          type="button"
                          onClick={() => updateStat(m.id, 'rating', Math.min(10.0, parseFloat((pStat.rating + 0.5).toFixed(1))))}
                          className="w-6 h-6 bg-[#a8c936]/20 hover:bg-[#a8c936]/30 text-[#a8c936] rounded flex items-center justify-center font-bold"
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <button
              onClick={() => setStep(4)}
              className="w-full py-3 bg-[#a8c936] hover:bg-[#b8db3d] text-[#080b0a] font-bold text-sm rounded-xl transition"
            >
              Next: Select Man of the Match
            </button>
          </div>
        )}

        {/* STEP 4: MOTM */}
        {step === 4 && (
          <div className="space-y-4">
            <span className="text-xs font-semibold text-slate-400 block">Select Man of the Match (MOTM)</span>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 max-h-[50vh] overflow-y-auto pr-1">
              {playingMembers.map((m) => {
                const pStat = playerStatsMap[m.id] || { goals: 0, assists: 0, rating: 6.5 };
                const isSelected = motmWinnerId === m.id;
                return (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => setMotmWinnerId(m.id)}
                    className={`p-3 rounded-xl border text-left flex items-center justify-between transition ${
                      isSelected
                        ? 'bg-[#1c221d] border-[#a8c936] text-white shadow-md'
                        : 'bg-[#171c19] border-white/5 text-slate-300 hover:text-white'
                    }`}
                  >
                    <div className="flex items-center gap-2.5">
                      <img src={m.avatar || 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100'} alt={m.displayName} className="w-9 h-9 rounded-full object-cover border border-white/10" />
                      <div>
                        <span className="font-semibold text-xs block">{m.displayName}</span>
                        <div className="flex items-center gap-2 text-[10px] text-slate-400 font-mono mt-0.5">
                          <span>{m.primaryPosition}</span>
                          <span>•</span>
                          <span className="text-[#a8c936] font-bold">Rating: {pStat.rating.toFixed(1)}</span>
                        </div>
                      </div>
                    </div>
                    {isSelected && <Star className="w-5 h-5 text-amber-400 fill-amber-400 shrink-0" />}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => setStep(5)}
              className="w-full py-3 bg-[#a8c936] hover:bg-[#b8db3d] text-[#080b0a] font-bold text-sm rounded-xl transition"
            >
              Next: Final Confirmation
            </button>
          </div>
        )}

        {/* STEP 5: PUBLISH */}
        {step === 5 && (
          <div className="space-y-6 py-2 text-center">
            <div className="bg-[#171c19] p-6 rounded-2xl border border-white/5 space-y-4">
              <div className="w-12 h-12 bg-[#a8c936]/10 border border-[#a8c936]/30 rounded-2xl flex items-center justify-center mx-auto text-[#a8c936]">
                <Trophy className="w-6 h-6" />
              </div>

              <div>
                <h4 className="text-lg font-bold text-white">Publish Official Match Report</h4>
                <p className="text-xs text-slate-400 font-medium max-w-sm mx-auto mt-1">
                  Final Score: <span className="text-[#a8c936] font-bold font-mono">{teamAScore} - {teamBScore}</span>. Career goals, assists, match ratings, form history, and leaderboards will be updated.
                </p>
              </div>

              {/* Summary Highlights */}
              <div className="bg-[#080b0a] p-3 rounded-xl border border-white/5 text-left text-xs space-y-1.5 font-mono">
                <div className="flex justify-between">
                  <span className="text-slate-400">Total Players Tracked:</span>
                  <span className="text-white font-bold">{playingMembers.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Man of the Match:</span>
                  <span className="text-amber-400 font-bold">
                    {members.find((m) => m.id === motmWinnerId)?.displayName || 'Not Selected'}
                  </span>
                </div>
              </div>
            </div>

            <button
              onClick={handleFinish}
              className="w-full py-3 bg-[#a8c936] hover:bg-[#b8db3d] text-[#080b0a] font-bold text-sm rounded-xl transition"
            >
              Publish Match Report
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
